import React, { ReactElement, useState, useEffect } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Autocomplete,
  Alert,
  Divider,
  Typography,
} from '@mui/material'
import { useForm } from '@tanstack/react-form'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import MotherIcon from 'src/v3/core/mother-icon'
import MotherThinking from 'src/v3/components/mother-thinking'
import {
  createPlanningEvent,
  updatePlanningEvent,
} from 'src/v3/content-pipeline/event-pipeline.slice'
import { modalInputSx, modalPaperProps } from 'src/v3/utilities/modal-styles'
import {
  selectCategories,
  selectCategoriesStatus,
  fetchCategories,
} from 'src/v3/events/events.slice'
import axios, { pluckData } from 'src/utilities/http-client'

type EventFormData = {
  name: string
  description: string
  hours: number
  category: string
  other?: string
  banner?: string
  thumbnail?: string
}

type EventCreationFormProps = {
  open: boolean
  onClose: () => void
  event?: any // PlanningEvent type
}

export default ({
  open,
  onClose,
  event,
}: EventCreationFormProps): ReactElement => {
  let dispatch = useAppDispatch()
  let [loading, setLoading] = useState(false)
  let [aiGenerating, setAiGenerating] = useState(false)
  let [generationProgress, setGenerationProgress] = useState<string[]>([])
  let [generationError, setGenerationError] = useState<string | null>(null)
  let [promptInput, setPromptInput] = useState('')
  let [showAiSection, setShowAiSection] = useState(false)
  let categories = useAppSelector(selectCategories)
  let categoriesStatus = useAppSelector(selectCategoriesStatus)

  // Fetch categories if needed
  useEffect(() => {
    if (categoriesStatus === 'uninitialized') {
      dispatch(fetchCategories())
    }
  }, [categoriesStatus, dispatch])

  // Category mapping from MOTHER API to frontend enum
  let mapCategory = (motherCategory: string): string => {
    let mapping: Record<string, string> = {
      'Airway/Respiratory/Ventilation': 'Airway|Respiratory|Ventilation',
      Cardiovascular: 'Cardiovascular',
      Trauma: 'Trauma',
      Medical: 'Medical',
      Pediatrics: 'Pediatrics',
      Operations: 'Operations',
      'Critical Care': 'Critical Care',
    }
    return mapping[motherCategory] || 'Medical' // Default fallback
  }

  let form = useForm({
    defaultValues: {
      name: event?.name || '',
      description: event?.description || '',
      hours: event?.hours || 0,
      category:
        typeof event?.category === 'object' && event.category !== null
          ? (event.category as any).kind === 'other'
            ? 'Other'
            : (event.category as any).kind
          : event?.category || '',
      other:
        typeof event?.category === 'object' &&
        event.category !== null &&
        (event.category as any).kind === 'other'
          ? (event.category as any).other
          : '',
      banner: event?.banner || '',
      thumbnail: event?.thumbnail || '',
    } as EventFormData,
    onSubmit: ({ value }) => {
      setLoading(true)
      try {
        let categoryValue =
          value.category === 'Other'
            ? { other: value.other || '' }
            : value.category
        let formData = {
          name: value.name,
          description: value.description,
          hours: value.hours,
          category: categoryValue,
          banner: '',
          thumbnail: '',
        }

        if (event) {
          dispatch(updatePlanningEvent({ ...event, ...formData }))
        } else {
          dispatch(createPlanningEvent(formData as any))
        }
        onClose()
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to save event:', error)
      } finally {
        setLoading(false)
      }
    },
  })

  // Start AI generation
  let handleAiGeneration = async () => {
    if (!promptInput.trim()) return

    // Ensure categories are loaded
    if (categoriesStatus === 'uninitialized') {
      dispatch(fetchCategories())
    }

    setAiGenerating(true)
    setGenerationProgress([])
    setGenerationError(null)

    try {
      // Start generation
      let { correlationID } = await axios
        .post<{
          correlationID: string
        }>('/api/pipeline/planning/events/generate', { prompt: promptInput })
        .then(pluckData)

      let correlationId = correlationID

      // Poll for status
      let pollInterval = setInterval(async () => {
        try {
          let status = await axios
            .get(
              `/api/pipeline/planning/events/generate/${correlationId}/status`,
            )
            .then(pluckData)

          // Update progress messages
          let progressMessages = status.progress.map((p: any) => p.message)
          setGenerationProgress(progressMessages)

          if (status.status === 'sessionCompleted' && status.result) {
            clearInterval(pollInterval)
            setAiGenerating(false)

            try {
              // Parse and validate AI result
              let { result } = status

              // Validate required fields
              if (!result.name || !result.description) {
                setGenerationError(
                  'AI result missing required fields (name, description)',
                )
                return
              }

              // Populate form with generated data
              let mappedCategory = mapCategory(result.category)

              form.setFieldValue('name', result.name)
              form.setFieldValue('description', result.description)
              form.setFieldValue('hours', result.hours || 1)
              form.setFieldValue('category', mappedCategory)

              setPromptInput('')
              setShowAiSection(false)
            } catch {
              // Handle malformed or invalid AI result
              setGenerationError(
                'The AI generated an invalid result. Please try again with a different prompt.',
              )
            }
          } else if (status.status === 'sessionFailed') {
            clearInterval(pollInterval)
            setAiGenerating(false)
            setGenerationError('AI generation failed. Please try again.')
          }
        } catch {
          clearInterval(pollInterval)
          setAiGenerating(false)
          setGenerationError('Failed to check generation status')
        }
      }, 2000) // Poll every 2 seconds
    } catch {
      setAiGenerating(false)
      setGenerationError('Failed to start AI generation')
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      slotProps={{
        paper: {
          ...modalPaperProps,
          sx: { ...modalPaperProps.sx, borderRadius: 2 },
        },
      }}
      maxWidth="md"
    >
      <DialogTitle>{event ? 'Edit Event' : 'Create New Event'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', gap: 3, mt: 1 }}>
          {/* AI Generation Section */}
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Typography variant="h6">Generate with AI</Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setShowAiSection(!showAiSection)}
                disabled={loading}
                startIcon={!showAiSection ? <MotherIcon /> : undefined}
              >
                {showAiSection ? 'Hide Generator' : 'Generate with MOTHER'}
              </Button>
            </Box>

            {showAiSection && (
              <Box
                sx={{ p: 2, bgcolor: 'background.paperDark', borderRadius: 1 }}
              >
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Describe your event"
                  placeholder="e.g., Create a 2-hour trauma course about managing severe bleeding in emergency situations"
                  value={promptInput}
                  onChange={(e) => setPromptInput(e.target.value)}
                  disabled={aiGenerating}
                  sx={{ mb: 2, bgcolor: 'background.paper' }}
                />

                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleAiGeneration}
                    disabled={aiGenerating || !promptInput.trim()}
                    size="small"
                  >
                    {aiGenerating ? 'Generating...' : 'Generate Event'}
                  </Button>
                  {aiGenerating && (
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setAiGenerating(false)
                        setGenerationProgress([])
                      }}
                      size="small"
                    >
                      Cancel
                    </Button>
                  )}
                </Box>

                {aiGenerating && (
                  <Box sx={{ mb: 2 }}>
                    <MotherThinking
                      isThinking={aiGenerating}
                      currentMessage={
                        generationProgress[generationProgress.length - 1] ||
                        'Generating event...'
                      }
                      size="medium"
                    />
                  </Box>
                )}

                {generationError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {generationError}
                  </Alert>
                )}
              </Box>
            )}

            <Divider sx={{ mt: 2 }} />
          </Box>
          <form.Field
            name="name"
            children={(field) => (
              <TextField
                label="Event Name"
                name={field.name}
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                error={field.state.meta.errors.length > 0}
                helperText={field.state.meta.errors[0]}
                required
                fullWidth
                sx={modalInputSx}
              />
            )}
          />

          <form.Field
            name="description"
            children={(field) => (
              <TextField
                label="Description"
                name={field.name}
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                error={field.state.meta.errors.length > 0}
                helperText={field.state.meta.errors[0]}
                required
                fullWidth
                sx={modalInputSx}
                multiline
                rows={3}
              />
            )}
          />

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <form.Field
              name="hours"
              children={(field) => (
                <TextField
                  label="Hours"
                  type="number"
                  name={field.name}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(Number(e.target.value))}
                  onBlur={field.handleBlur}
                  error={field.state.meta.errors.length > 0}
                  helperText={field.state.meta.errors[0]}
                  required
                  fullWidth
                  sx={modalInputSx}
                  slotProps={{
                    htmlInput: { min: 0, step: 0.5 },
                  }}
                />
              )}
            />

            <form.Field
              name="category"
              children={(field) => (
                <FormControl fullWidth required>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={field.state.value}
                    label="Category"
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    sx={modalInputSx}
                  >
                    <MenuItem value="">Unselected</MenuItem>
                    {categories.standard?.map((cat) => (
                      <MenuItem key={cat} value={cat}>
                        {cat.replaceAll('|', '/')}
                      </MenuItem>
                    ))}
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </Box>

          {/* Other category input */}
          <form.Field
            name="category"
            children={(categoryField) =>
              categoryField.state.value === 'Other' && (
                <form.Field
                  name="other"
                  children={(otherField) => (
                    <Autocomplete
                      fullWidth
                      freeSolo
                      options={categories.custom || []}
                      value={otherField.state.value}
                      onChange={(_, value) =>
                        otherField.handleChange(value || '')
                      }
                      onBlur={otherField.handleBlur}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Custom Category"
                          required
                          sx={{
                            '& .MuiInputBase-root': {
                              bgcolor: 'background.paperDark',
                            },
                          }}
                        />
                      )}
                    />
                  )}
                />
              )
            }
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading || aiGenerating}>
          Cancel
        </Button>
        <Button
          onClick={() => form.handleSubmit()}
          variant="contained"
          disabled={loading || aiGenerating}
        >
          {loading ? 'Saving...' : event ? 'Update Event' : 'Create Event'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
